import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';

import type { Service } from 'webapp-types';
import { GoalStatus, GoalType } from 'webapp/enums/enums';

import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import type { HtmlEditorOptions } from 'webapp/components/form/html-editor';
import FormHtmlEditorComponent from 'webapp/components/form/html-editor';
import GoalsDonutComponent from 'webapp/components/goals/donut';

import type { Goal } from 'webapp/models-custom';

export interface OverviewComponentArgs {
	goals: Goal[];
	toggleOverview: () => void;
	showEmptyState?: boolean;
}

export default class OverviewComponent extends Component<OverviewComponentArgs> {
	@service('user-settings') declare userSettingsService: Service.UserSettings;
	@service('model-manager/goals') declare mmGoalsService: Service.ModelManager.Goals;
	@service('account') declare accountService: Service.Account;
	@service('current-user') declare currentUserService: Service.CurrentUser;

	companyStrategyPlaceholder = 'Click to add company strategy...';
	htmlEditorOptions: HtmlEditorOptions = {
		placeholder: this.companyStrategyPlaceholder,
		toolbar: 'bold italic underline | alignleft aligncenter alignright',
		plugins: ['wordcount', 'paste'],
		contextmenu: false,
		// paste plugin
		paste_block_drop: false,
		smart_paste: false,
		paste_as_text: true,
	};

	get goals() {
		return this.args.goals;
	}

	get dataForObjectivesDonut() {
		return this.getDataForDonutByGoalType(GoalType.OBJECTIVE);
	}

	get dataForGoalsDonut() {
		return this.getDataForDonutByGoalType(GoalType.GOAL);
	}

	get companyStrategy() {
		return this.mmGoalsService.companyStrategy;
	}

	get isCompanyStrategyEditEnabled(): boolean {
		return (
			!this.accountService.isDemoAccount &&
			(this.currentUserService.isEditor || this.currentUserService.isAdmin)
		);
	}

	@action
	onCompanyStrategyChange(newCompanyStrategy: string): void {
		if (newCompanyStrategy === this.companyStrategy) {
			return;
		}

		void this.mmGoalsService.updateCompanyStrategy(newCompanyStrategy);
	}

	private getDataForDonutByGoalType(type: GoalType) {
		const statusCounts: Record<string, number> = {
			[GoalStatus.ON_TRACK]: 0,
			[GoalStatus.AT_RISK]: 0,
			[GoalStatus.OFF_TRACK]: 0,
			[GoalStatus.OTHER]: 0,
		};

		for (const goal of this.goals) {
			if (goal.type === type) {
				const status = goal.hierarchyDataModelWithFallback?.status;
				if (status && status in statusCounts) {
					statusCounts[String(status)]!++;
				} else {
					statusCounts[GoalStatus.OTHER]!++;
				}
			}
		}

		return [
			{ status: GoalStatus.ON_TRACK, count: statusCounts[GoalStatus.ON_TRACK]! },
			{ status: GoalStatus.AT_RISK, count: statusCounts[GoalStatus.AT_RISK]! },
			{ status: GoalStatus.OFF_TRACK, count: statusCounts[GoalStatus.OFF_TRACK]! },
			{ status: GoalStatus.OTHER, count: statusCounts[GoalStatus.OTHER]! },
		];
	}
	<template>
		<div class="overview">
			<div class="title-action">
				<span class="title">Overview</span>
				{{#unless @showEmptyState}}
					<div
						role="button"
						class="button theme-blue-empty"
						{{on "click" @toggleOverview}}
					>
						<CommonUiIconComponent @name="EyeClosed" @size="sm" />Hide
					</div>
				{{/unless}}
			</div>
			<div class="divider"></div>
			<div class="content">
				<div class="content-row content-row-left">
					<div class="content-column">
						<div class="title">Strategy</div>
						{{#if this.isCompanyStrategyEditEnabled}}
							<FormHtmlEditorComponent
								@text={{this.companyStrategy}}
								@onChange={{this.onCompanyStrategyChange}}
								@options={{this.htmlEditorOptions}}
								@maxChars={{1000}}
							/>
						{{else}}
							<div class="strategy-text">{{{this.companyStrategy}}}</div>
						{{/if}}
					</div>
				</div>
				<div class="content-row">
					<div class="content-column">
						<div class="title">
							<CommonUiIconComponent @name="Flag" @size="xl" />
							Objectives
						</div>
						<GoalsDonutComponent
							@data={{this.dataForObjectivesDonut}}
							@showEmptyState={{@showEmptyState}}
						/>
					</div>
					<div class="content-column">
						<div class="title">
							<CommonUiIconComponent @name="Target" @size="xl" />
							Goals
						</div>
						<GoalsDonutComponent
							@data={{this.dataForGoalsDonut}}
							@showEmptyState={{@showEmptyState}}
						/>
					</div>
				</div>
			</div>
		</div>
	</template>
}
